"use client";

import React from "react";
import { Calendar, User, Clock, ArrowRight, Share2, ChevronLeft, Tag } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";


const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};


function RelatedPostsList() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="12604" className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-96 rounded-lg bg-muted/30 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <div key={post.id} data-index={i} className="group flex flex-col h-full">
          <Link to={post.link || "#"} className="block overflow-hidden rounded-lg mb-4">
            <div className="aspect-[4/3] bg-muted relative overflow-hidden">
              {post.featuredImage ? (
                <img 
                  src={post.featuredImage} 
                  alt={post.title} 
                  className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-accent text-muted-foreground">
                  <span className="text-sm font-sans">Image Pending</span>
                </div>
              )}
            </div>
          </Link>
          <div className="flex flex-col flex-grow">
            <div className="text-xs font-sans font-semibold text-primary/80 mb-2 uppercase tracking-widest">
              {post.categories?.[0]?.name || "Governance"}
            </div>
            <h3 className="text-xl font-sans font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2 text-foreground">
              <Link to={post.link || "#"}>{post.title}</Link>
            </h3>
            <div className="text-muted-foreground text-sm font-sans line-clamp-3 mb-4 flex-grow" dangerouslySetInnerHTML={{ __html: post.excerpt || "" }} />
            <Link to={post.link || "#"} className="inline-flex items-center text-sm font-bold text-primary hover:text-secondary transition-colors mt-auto">
              Read Analysis <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}

function RelatedPostsSection({ currentPostId }: { currentPostId: string }) {
  
  const relatedQuery = new WP_Query({
    post_type: 'post',
    posts_per_page: 3,
    post__not_in: [parseInt(currentPostId) || 0],
    orderby: 'date',
    order: 'DESC'
  });

  return (
    <div className="mt-20 pt-12 border-t border-border">
      <div className="flex items-center justify-between mb-10">
        <h3 className="text-3xl font-sans font-bold text-foreground">Related Insights</h3>
        <Link to="#">
          <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white rounded-sm">View All Resources</Button>
        </Link>
      </div>
      <PostsProvider wp_query={relatedQuery}>
        <RelatedPostsList />
      </PostsProvider>
    </div>
  );
}


function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-20 max-w-4xl">
        <div className="h-8 w-32 bg-muted animate-pulse mb-6 mx-auto rounded" />
        <div className="h-16 w-3/4 bg-muted animate-pulse mb-8 mx-auto rounded" />
        <div className="h-96 w-full bg-muted animate-pulse rounded-xl mb-12" />
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted animate-pulse rounded" />
          <div className="h-4 w-full bg-muted animate-pulse rounded" />
          <div className="h-4 w-5/6 bg-muted animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-destructive mb-4 font-sans">Error Loading Content</h2>
        <p className="text-muted-foreground font-sans">We couldn't load the requested report. Please try again later.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold mb-4 font-sans text-foreground">Article Not Found</h2>
        <Link to="/HomePage">
          <Button className="bg-primary hover:bg-primary/90 rounded-sm">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto px-4 py-12 md:py-20 max-w-4xl">
      {}
      <div className="mb-8">
        <Link to="#">
          <Button variant="ghost" size="sm" className="pl-0 hover:bg-transparent text-primary hover:text-primary/70 font-sans font-semibold">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Resources
          </Button>
        </Link>
      </div>

      {}
      <header className="text-center mb-10 md:mb-14">
        <div className="flex items-center justify-center gap-3 text-xs font-bold text-primary mb-6 uppercase tracking-[0.2em]">
          {post.categories && post.categories.length > 0 && (
            <span className="flex items-center gap-1">
              <Tag className="w-3 h-3" />
              {post.categories[0].name}
            </span>
          )}
          {post.date && (
            <>
              <span className="w-1 h-1 rounded-full bg-primary/30" />
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(post.date)}
              </span>
            </>
          )}
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-sans font-bold text-foreground leading-[1.1] mb-8 tracking-tight">
          {post.title}
        </h1>

        {}
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border border-border">
              <AvatarImage src={post.author?.avatar?.['96'] || ""} alt={post.author?.name} />
              <AvatarFallback className="bg-accent text-primary"><User className="w-5 h-5" /></AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-bold text-foreground font-sans">{post.author?.name || "Kanha ERP Editorial"}</p>
              <p className="text-xs text-muted-foreground font-sans">Kanha ERP Specialist</p>
            </div>
          </div>
          
          <div className="hidden md:block w-px h-8 bg-border" />
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-sm h-9 border-border hover:bg-accent text-foreground font-sans">
              <Share2 className="w-4 h-4 mr-2" /> Share Insight
            </Button>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 md:mb-16 rounded-xl overflow-hidden shadow-md bg-muted">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-auto object-cover max-h-[600px]"
          />
        </div>
      )}

      {}
      <div className="prose prose-lg dark:prose-invert max-w-none font-sans text-foreground/90 
        prose-headings:font-sans prose-headings:font-bold prose-headings:text-foreground prose-headings:tracking-tight
        prose-a:text-primary prose-a:no-underline prose-a:font-semibold prose-a:border-b prose-a:border-primary/20 hover:prose-a:border-primary
        prose-img:rounded-lg prose-img:shadow-md
        prose-blockquote:border-l-secondary prose-blockquote:bg-accent/50 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:not-italic prose-blockquote:text-foreground
        [&_p]:mb-6 [&_p]:leading-relaxed
        [&_h2]:text-3xl [&_h2]:mt-12 [&_h2]:mb-6
        [&_h3]:text-2xl [&_h3]:mt-8 [&_h3]:mb-4
        [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_li]:mb-2
        ">
        <div dangerouslySetInnerHTML={{ __html: post.content || "" }} />
      </div>

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span key={tag.id} className="px-3 py-1 bg-accent text-primary text-xs font-bold rounded-sm uppercase tracking-wider">
                #{tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {}
      <div className="mt-16 bg-accent/30 rounded-xl p-8 md:p-10 flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-8 border border-border shadow-sm">
        <Avatar className="h-20 w-20 md:h-24 md:w-24 border-4 border-white shadow-md">
          <AvatarImage src={post.author?.avatar?.['96'] || ""} alt={post.author?.name} />
          <AvatarFallback className="bg-primary text-white"><User className="w-10 h-10" /></AvatarFallback>
        </Avatar>
        <div className="text-center md:text-left flex-1">
          <h4 className="text-xl font-bold font-sans mb-2 text-foreground">About {post.author?.name || "Our Specialist"}</h4>
          <p className="text-muted-foreground font-sans leading-relaxed mb-4">
            Dedicated expert in public sector ERP solutions at Kanha ERP. Focused on driving operational excellence through digital transformation and regulatory compliance for government agencies worldwide.
          </p>
          <Link to={post.author?.link || "#"}>
            <Button variant="link" className="p-0 h-auto text-primary font-bold hover:text-secondary">View Professional Profile</Button>
          </Link>
        </div>
      </div>

      {}
      <RelatedPostsSection currentPostId={post.id} />
    </article>
  );
}


export default function SinglePostTemplate() {
  
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post" };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen w-full text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}